// TODO properly export MessagingRoot from @lookiero/messaging-react
import React, { ComponentType, FC, useCallback } from "react";
import { Platform } from "react-native";
import { useRoutes as reactRouterUseRoutes } from "react-router";
import { BANNER_TYPE, ViewProperties } from "@lookiero/aurora";
import { OnI18nErrorFn } from "@lookiero/i18n-react";
import { MessagingRoot } from "@lookiero/messaging-react/bootstrap";
import { Locale } from "@lookiero/sty-psp-locale";
import {
  SentryEnvironment,
  SentryLoggerFunctionArgs,
  sentryLogger,
  sentryLoggerHOC,
} from "@lookiero/sty-psp-logging/sentry";
import { Layout } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { i18n } from "./i18n/i18n";
import { Routing } from "./routing/Routing";
import { Session } from "./shared/types";

interface RafBannerProps extends ViewProperties {
  readonly bannerType?: BANNER_TYPE;
  readonly scaledBanner?: boolean;
}

type RafComponent = FC<RafBannerProps>;

interface RootComponentProps {
  readonly basePath?: string;
  readonly session: Session;
  readonly cdnPath: string;
  readonly layout: Layout;
  readonly rafComponent: RafComponent;
  readonly tradename: Tradename;
  readonly locale?: Locale;
  readonly apiUrl: () => string;
  readonly useRedirect: () => Record<string, string>;
  readonly useRoutes?: typeof reactRouterUseRoutes;
  readonly onBack: () => void;
  readonly onBackToStyleProfile: () => void;
  readonly onPressLookAndLikeBanner: () => void;
  readonly onShowReturnInstructions: (serviceId: string) => void;
  readonly onNotAccessible: () => void;
}

type RootComponent = ComponentType<RootComponentProps>;

interface RootFunctionArgs {
  readonly Messaging: MessagingRoot;
  readonly development?: boolean;
  readonly sentry: () => SentryEnvironment;
}

interface RootFunction {
  (args: RootFunctionArgs): (args: SentryLoggerFunctionArgs) => RootComponent;
}

const root: RootFunction = ({ Messaging, development, sentry }) => {
  const logger = sentryLogger(sentry);

  const Root: RootComponent = ({
    basePath = "",
    session,
    cdnPath,
    layout,
    rafComponent,
    tradename,
    locale = Locale.en_GB,
    apiUrl,
    useRedirect,
    useRoutes = reactRouterUseRoutes,
    onBack,
    onBackToStyleProfile,
    onPressLookAndLikeBanner,
    onShowReturnInstructions,
    onNotAccessible,
  }: RootComponentProps) => {
    const handleOnI18nError = useCallback<OnI18nErrorFn>(
      (error, fingerprint) =>
        logger.captureException(error, fingerprint ? { captureContext: { fingerprint: [...fingerprint] } } : undefined),
      [],
    );

    return (
      <Messaging includeReactQueryDevTools={Platform.OS === "web"}>
        <Routing
          I18n={i18n({ tradename })}
          apiUrl={apiUrl}
          basePath={basePath}
          cdnPath={cdnPath}
          layout={layout}
          locale={locale}
          rafComponent={rafComponent}
          session={session}
          tradename={tradename}
          useRedirect={useRedirect}
          useRoutes={useRoutes}
          onBack={onBack}
          onBackToStyleProfile={onBackToStyleProfile}
          onI18nError={development ? undefined : handleOnI18nError}
          onNotAccessible={onNotAccessible}
          onPressLookAndLikeBanner={onPressLookAndLikeBanner}
          onShowReturnInstructions={onShowReturnInstructions}
        />
      </Messaging>
    );
  };

  const hoc = sentryLoggerHOC({ logger });

  /**
   * It's important to not call sentryLoggerHOC each time this function gets called.
   * That's why its called outside this method.
   */
  return ({ customerId }) => hoc({ customerId })(Root);
};

export type { RafComponent, RootComponent };
export { root };
